import React, { useState, useEffect, useRef } from 'react';
import { 
    MessageCircle, Send, 
} from 'lucide-react';

const CommentSection = ({ comments, newComment, setNewComment, onSendComment, currentUser }) => {
    // 1. Create a ref for the scrollable container
    const chatContainerRef = useRef(null);

    // 2. Effect to scroll to bottom when comments change
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTo({
                top: chatContainerRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [comments]); // Runs every time comments array is updated

    return (
        <div className="flex flex-col h-[500px] animate-in fade-in slide-in-from-bottom-4 duration-500 -mx-2">
            {/* Chat Area - Added ref and ensure overflow-y-auto is present */}
            <div 
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto px-2 custom-scrollbar space-y-6 pb-6 flex flex-col"
            >
                {comments.length > 0 ? (
                    comments.map((msg) => {
                        const isMe = currentUser && msg.user.name === currentUser.name;
                        return (
                            <div key={msg.id} className={`flex gap-3 w-full ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                {!isMe && (
                                    <img 
                                        src={msg.user.avatarUrl} 
                                        alt={msg.user.name} 
                                        className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0 mt-auto"
                                        onError={(e) => { e.target.src = 'https://i.pravatar.cc/150?u=placeholder' }}
                                    />
                                )}
                                <div className={`flex flex-col max-w-[85%] sm:max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
                                    <div className={`flex items-baseline gap-2 mb-1.5 px-1 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                        <span className="text-sm font-black text-slate-700">{isMe ? 'You' : msg.user.name}</span>
                                        {!isMe && <span className="text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-md">{msg.user.role}</span>}
                                        <span className="text-[10px] font-bold uppercase text-slate-400">
                                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                    <div className={`text-sm font-medium p-4 shadow-sm break-words ${isMe 
                                        ? 'bg-indigo-600 text-white rounded-2xl rounded-br-sm' 
                                        : 'bg-white ring-1 ring-slate-200/80 text-slate-800 rounded-2xl rounded-bl-sm'}`}>
                                        {msg.text}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center opacity-70 m-auto">
                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 ring-1 ring-slate-200">
                            <MessageCircle className="w-8 h-8 text-slate-400" />
                        </div>
                        <p className="text-base font-bold text-slate-700">No comments yet</p>
                    </div>
                )}
            </div>
            
            {/* Input Area */}
            <div className="pt-4 mt-auto shrink-0 px-2">
                <div className="flex items-end gap-3 bg-white ring-1 ring-slate-200 p-2 rounded-[1.25rem] focus-within:ring-indigo-500 focus-within:ring-2 focus-within:shadow-md transition-all shadow-sm">
                    <textarea 
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSendComment(); }}}
                        placeholder="Write a comment..."
                        className="flex-1 bg-transparent border-none px-3 py-2 text-sm font-medium focus:outline-none resize-none max-h-32 min-h-[44px] text-slate-800 leading-relaxed"
                        rows="1"
                    />
                    <button 
                        onClick={onSendComment}
                        disabled={!newComment.trim()}
                        className="bg-indigo-600 text-white p-3 rounded-xl hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 h-11 w-11 flex items-center justify-center active:scale-95"
                    >
                        <Send size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CommentSection;